import { MenoUserData, NervaUserData, PainUserData, QuitUserData } from 'hydra-data-types';
import React, { ComponentProps, FunctionComponent, ReactElement } from 'react';
import { ArrayUtils, capitalise } from 'ts-utils';
import { LayoutScreenConfig, ScreenType } from '../../config/FlowTypes';
import { AppSelector, useAppStore } from '../../store/Store';
import { appOverride, appVariant } from '../../util/AppUtils';
import { choicesToLabels } from '../../util/FlowUtils';
import { translate } from '../../util/Localization';
import { DrElkinsCard, DrPetersCard } from '../DrCard';
import { ScreenWrapper } from '../styles/Containers';
import { ProgramSummary, ProgramSummaryProgramField } from '../styles/ProgramSummary';
import { BridgeType } from '../styles/StepList';
import { NavigationProps } from './FlowNavigation';
import { PresentationContent, PresentationLayout } from './layouts/PresentationLayout';

type ProgramFields = ComponentProps<typeof ProgramSummary>['programFields'];

type ProgramStructureLayoutConfig = {
  title?: string;
  body?: string[];
  additionalProgramFields?: ProgramSummaryProgramField[];
  coreProgramWeekTitles: string[];
  postProgramTitle?: string;
  postProgramContent: string;
  primaryButtonTitle: string;
  hideDoc?: boolean;
};

export type ProgramStructureScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.ProgramStructure,
  ProgramStructureLayoutConfig,
  ScreenNames
>;

type ProgramStructureProps<ScreenNames extends string> = NavigationProps<
  ProgramStructureScreenConfig<ScreenNames>,
  ScreenNames
>;

export const ProgramStructureScreen = <ScreenNames extends string>(
  props: ProgramStructureProps<ScreenNames>
): ReactElement => {
  const { navigation, layout } = props;
  const { userData, user } = useAppStore((state) => ({
    userData: AppSelector.userData(state),
    user: AppSelector.user(state),
  }));

  function handlePrimaryButtonPress() {
    navigation.next(props);
  }

  function parseGoals() {
    const manageProgramIntentAnswers = choicesToLabels((userData as MenoUserData).programIntent);

    const menoAnswersString =
      'Manage ' + ArrayUtils.toHumanReadableString(manageProgramIntentAnswers).toLowerCase();

    const nervaAnswers = (userData as NervaUserData).howLikeToFeel ?? [];

    const quitAnswers = choicesToLabels((userData as QuitUserData).howLikeToFeel);

    const painAnswers = (userData as PainUserData).whatWouldChange ?? [];

    return appVariant({
      meno: menoAnswersString,
      nerva: ArrayUtils.toHumanReadableString(nervaAnswers),
      quit: ArrayUtils.toHumanReadableString(quitAnswers),
      pain: ArrayUtils.toHumanReadableString(painAnswers),
    });
  }

  function parseFocus() {
    const focusItems = appVariant({
      meno: [], // Focus items are not shown for meno
      nerva: choicesToLabels((userData as NervaUserData).classify),
      quit: ['Nicotine Dependence', 'Emotions', 'Habits'], // Hardcoded for now
      pain: ['Back pain management'],
    });
    return ArrayUtils.toHumanReadableString(focusItems);
  }

  function parseSymptoms() {
    const symptomItems = appOverride({
      default: [],
      nerva: choicesToLabels((userData as NervaUserData).symptoms),
      quit: choicesToLabels((userData as QuitUserData).symptoms),
      // Pain does not pull these into the Program Experience screen, it has Goals.
    });
    return ArrayUtils.toHumanReadableString(symptomItems);
  }

  function parseTriggers() {
    const triggersItems = appOverride({
      default: [],
      nerva: choicesToLabels((userData as NervaUserData).triggers), // Focus items are shown only for Nerva
    });
    return ArrayUtils.toHumanReadableString(triggersItems);
  }

  function parseOtherSymptoms() {
    const symptomItems = appOverride({
      default: [],
      quit: choicesToLabels((userData as QuitUserData).impactLife),
    });
    return ArrayUtils.toHumanReadableString(symptomItems);
  }

  function parseHCP() {
    const hcpItem = appOverride({
      default: [],
      nerva: choicesToLabels((userData as NervaUserData).hcpName),
    });
    return ArrayUtils.toHumanReadableString(hcpItem);
  }

  function parseFrequency() {
    const frequency = appOverride({
      default: [],
      pain: choicesToLabels((userData as PainUserData).daysPerWeekPain).map(
        (days) => `${days} per week`
      ),
    });
    return ArrayUtils.toHumanReadableString(frequency);
  }

  function parseTimeCommitment() {
    const timeCommitment = appOverride({
      default: ['15 minutes per day'],
      pain: choicesToLabels((userData as PainUserData).timeCommitment).map(
        (time) => `${time} per day`
      ),
    });
    return ArrayUtils.toHumanReadableString(timeCommitment);
  }

  function parseProgramFields(): ProgramFields {
    const focus = { field: 'Focus', answers: parseFocus() };
    const symptoms = {
      // Neither Pain nor meno include 'symptoms' in the return value from this function,
      // but even though we don't need it, we still populate this 'symptoms' field, so we still need
      // to pass a `pain:` value to appVariant here. I have intentionally misspelled `Symptons` so we can
      // detect when it is being used when we don't expect to to be used in future, while still giving
      // a word that the user will probably read asthe correctly spelled 'Symptoms'.
      field: appVariant({
        nerva: 'Symptoms',
        meno: 'Symptoms',
        quit: 'Physical symptoms',
        pain: 'Symptons',
      }),
      answers: parseSymptoms(),
    };
    const otherSymptoms = {
      field: 'Other symptoms',
      answers: parseOtherSymptoms(),
    };
    const triggers = { field: 'Triggers', answers: parseTriggers() };
    const goals = { field: 'Goals', answers: parseGoals() };
    const hcp = { field: 'Practitioner', answers: parseHCP() };
    const time = { field: 'Time', answers: parseTimeCommitment() };
    const frequency = { field: 'Frequency', answers: parseFrequency() };

    return appVariant<ProgramFields>({
      meno: [goals, time],
      nerva: [focus, symptoms, triggers, goals, time, hcp],
      quit: [goals, time, symptoms, otherSymptoms],
      pain: [focus, frequency, time, goals],
    }).filter((x) => x.answers !== '');
  }

  const programSummaryTitle =
    user?.type === 'partial'
      ? translate('program')
      : translate('userProgram', { user: capitalise(user?.name) ?? '' });

  const programSummary = (
    <ProgramSummary
      title={programSummaryTitle}
      programFields={[...parseProgramFields(), ...(layout.additionalProgramFields ?? [])]}
      programContents={{
        bridgeType: BridgeType.Line,
        items: layout.coreProgramWeekTitles.map((desc, i) => ({
          title: translate('programSummaryWeekTitle', { weekNumber: i + 1 }),
          desc,
        })),
        lastItemBridge: true,
      }}
      postProgramTitle={layout.postProgramTitle}
      postProgramContent={layout.postProgramContent}
    />
  );

  const presentationContent: PresentationContent[] = [
    ...(layout.body ?? []).map<PresentationContent>((content) => ({
      type: 'text',
      content,
    })),
    { type: 'custom', content: programSummary },
  ];
  if (!layout.hideDoc) {
    if (DoctorCard) {
      presentationContent.push({ type: 'custom', content: <DoctorCard /> });
    }
  }

  return (
    <ScreenWrapper>
      <PresentationLayout
        title={layout.title}
        content={presentationContent}
        primaryButtonTitle={layout.primaryButtonTitle}
        onPressPrimaryButton={handlePrimaryButtonPress}
      />
    </ScreenWrapper>
  );
};
// Pain does not display a doctor card (it has hideDoc===true).
const DoctorCard = appVariant<FunctionComponent | null>({
  meno: DrElkinsCard,
  nerva: DrPetersCard,
  quit: DrElkinsCard,
  pain: null,
});
